import React, { useRef } from "react";
import Resources from "../../types/Resources";
import Department from "../../types/Department";
import { CodeStatus, HarbourOffice, PermitCategory, PermitTypeFilter } from "../../common/enums";
import { Transformation } from "../../types/functions";
import useDateInput from "../../hooks/useDateInput";
import { Date_dateStringIsValid, Date_isSameDate, Date_toDateString } from "../../common/dateUtils";
import WorkPermitFilterDropdown from "./WorkPermitFilterDropdown";
import WorkPermitFilter from "../../types/filters/WorkPermitFilter";

type DateLabelStyle = "dark" | "light";

export type WorkPermitFilterControlProps = {
    filter: WorkPermitFilter;
    resources: Resources;
    allDepartmentOptions: Department[];
    allStatusOptions: CodeStatus[];
    allPermitCategoryOptions: PermitCategory[];
    allHarbourOfficeOptions: HarbourOffice[];
    allEnergyHarbourPermitTypeOptions: PermitTypeFilter[];
    allPortControlPermitTypeOptions: PermitTypeFilter[];
    dateLabelStyle?: DateLabelStyle;
    onApplyFilter: (options?: { fromDate?: Date; toDate?: Date }) => void;
    onFilterChange: (transformation: Transformation<WorkPermitFilter>) => void;
};

function WorkPermitFilterControls(props: WorkPermitFilterControlProps) {
    const fromDateInputRef = useRef<HTMLInputElement>(null);
    const toDateInputRef = useRef<HTMLInputElement>(null);

    const fromDateManager = useDateInput({
        comparer: Date_isSameDate,
        date: props.filter.fromDate,
        onDateChange: (newDate) => filterChangeHandler("fromDate", newDate),
        ref: fromDateInputRef,
        toStringValue: Date_toDateString
    });

    const toDateManager = useDateInput({
        comparer: Date_isSameDate,
        date: props.filter.toDate,
        onDateChange: (newDate) => filterChangeHandler("toDate", newDate),
        ref: toDateInputRef,
        toStringValue: Date_toDateString
    });

    function filterChangeHandler(key: keyof WorkPermitFilter, value: any) {
        props.onFilterChange((prevFilter) => {
            return { ...prevFilter, [key]: value };
        });
    }

    function keyDownHandler(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();

            // If a change is made to the date inputs, the state is not updated until the input loses focus (onBlur). But if the user presses Enter, we want to apply the filter with the current
            // values in the field, so we pass the values from the refs directly.
            const fromDate =
                !!fromDateInputRef.current && Date_dateStringIsValid(fromDateInputRef.current.value)
                    ? new Date(fromDateInputRef.current.value)
                    : fromDateManager.date;
            const toDate =
                !!toDateInputRef.current && Date_dateStringIsValid(toDateInputRef.current.value)
                    ? new Date(toDateInputRef.current.value)
                    : toDateManager.date;

            props.onApplyFilter({ fromDate, toDate });
        }
    }

    function checkboxChangeHandler<TValue>(key: keyof WorkPermitFilter, value: TValue, checked: boolean) {
        props.onFilterChange((prevValue) => {
            const prevValues = (prevValue[key] || []) as TValue[];
            const newValues = checked ? [...prevValues, value] : prevValues.filter((v) => v !== value);

            return {
                ...prevValue,
                [key]: newValues
            };
        });
    }

    function departmentsChangeHandler(selectedDepartments: Department[]) {
        props.onFilterChange((prevValue) => {
            const departmentIds = selectedDepartments.map((d) => d.id);
            return {
                ...prevValue,
                departmentIds: departmentIds
            };
        });
    }

    return (
        <div onKeyDown={keyDownHandler} className="d-flex">
            <div className="work-permit-filter__search-input">
                <input
                    className="form-control"
                    id="search-text-input"
                    value={props.filter.searchText ?? ""}
                    placeholder={props.resources.get("Search")}
                    onChange={(e) => filterChangeHandler("searchText", e.target.value)}
                    maxLength={100}
                />
            </div>

            <div className="work-permit-filter__date-input me-3">
                {!props.dateLabelStyle || props.dateLabelStyle === "dark" ? (
                    <label className="h6 m-0 me-1" htmlFor="work-permit-filter__from-date-input">
                        {props.resources.get("From")}
                    </label>
                ) : (
                    <>
                        <div className="work-permit-filter__date-label-background"></div>
                        <label className="h6 m-0 me-1" htmlFor="work-permit-filter__from-date-input">
                            {props.resources.get("From")}
                        </label>
                    </>
                )}
                <input
                    id="work-permit-filter__from-date-input"
                    className="form-control"
                    type="date"
                    defaultValue={Date_toDateString(fromDateManager.date)}
                    onBlur={fromDateManager.blurHandler}
                    ref={fromDateInputRef}
                />
            </div>

            <div className="work-permit-filter__date-input">
                {!props.dateLabelStyle || props.dateLabelStyle === "dark" ? (
                    <label className="h6 m-0 me-1" htmlFor="work-permit-filter__to-date-input">
                        {props.resources.get("To")}
                    </label>
                ) : (
                    <>
                        <div className="work-permit-filter__date-label-background"></div>
                        <label className="h6 m-0 me-1" htmlFor="work-permit-filter__to-date-input">
                            {props.resources.get("To")}
                        </label>
                    </>
                )}
                <input
                    id="work-permit-filter__to-date-input"
                    className="form-control"
                    type="date"
                    defaultValue={Date_toDateString(toDateManager.date)}
                    onBlur={toDateManager.blurHandler}
                    ref={toDateInputRef}
                />
            </div>

            <WorkPermitFilterDropdown
                allDepartmentOptions={props.allDepartmentOptions}
                allStatusOptions={props.allStatusOptions}
                allPermitCategoryOptions={props.allPermitCategoryOptions}
                allHarbourOfficeOptions={props.allHarbourOfficeOptions}
                allEnergyHarbourPermitTypeOptions={props.allEnergyHarbourPermitTypeOptions}
                allPortControlPermitTypeOptions={props.allPortControlPermitTypeOptions}
                filter={props.filter}
                resources={props.resources}
                onApplyFilter={props.onApplyFilter}
                onCheckboxChange={checkboxChangeHandler}
                onSelectedDepartmentsChange={departmentsChangeHandler}
            />
        </div>
    );
}

export default WorkPermitFilterControls;
